/**
 * PayTomorrow UI Fix (Duallys Only)
 *
 * Goals:
 * 1) Increase the "$xx/mo with" line by +4px
 * 2) Make "Learn More" the same font size as the "$xx/mo with" line
 * 3) Make "Learn More" red to match the monthly amount
 * 4) Apply site-wide (product pages + collection/home product cards)
 */
(function () {
  const LEARN_MORE_COLOR = '#d60000';
  const STYLE_ID = 'do-ptm-ui-fix-styles';

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      /* Force PayTomorrow offer line to stay inline (cards + product page) */
      [class*="ptm"],
      .ptm-price-wrapper,
      .ptm-offer,
      .ptm-messaging {
        max-width: 100%;
      }

      .ptm-price-wrapper,
      .ptm-offer,
      .ptm-messaging {
        display: inline-flex !important;
        align-items: baseline !important;
        gap: 6px !important;
        flex-wrap: nowrap !important;
        white-space: nowrap !important;
        line-height: 1.2 !important;
      }

      /* Make sure Learn More never becomes block/huge */
      .ptm-price-wrapper a,
      .ptm-offer a,
      .ptm-messaging a {
        display: inline !important;
        line-height: 1.2 !important;
        text-transform: none !important;
      }

      /* Keep PayTomorrow branding from inheriting weird styles */
      .ptm-price-wrapper img,
      .ptm-offer img,
      .ptm-messaging img {
        max-height: 18px;
        height: auto;
        width: auto;
        vertical-align: middle;
      }
    `;
    document.head.appendChild(style);
  }

  function isElement(el) {
    return !!el && el.nodeType === 1;
  }

  function textIncludes(el, re) {
    if (!isElement(el)) return false;
    const t = (el.textContent || '').trim();
    return re.test(t);
  }

  // Find a PayTomorrow link inside container. Prefer the "Learn More" link.
  function findPTLink(container) {
    if (!isElement(container)) return null;
    const anchors = Array.from(container.querySelectorAll('a'));
    const payLinks = anchors.filter((a) => (a.getAttribute('href') || '').toLowerCase().includes('paytomorrow'));
    const learnMore = payLinks.find((a) => /learn\s*more/i.test((a.textContent || '').trim())) ||
      anchors.find((a) => /learn\s*more/i.test((a.textContent || '').trim()));
    return learnMore || payLinks[0] || null;
  }

  // Walk up from the PayTomorrow link to find a reasonable wrapper.
  function getOfferWrapperFromLink(link) {
    if (!isElement(link)) return null;
    return (
      link.closest('.ptm-price-wrapper') ||
      link.closest('.ptm-offer') ||
      link.closest('.ptm-messaging') ||
      link.closest('[class*="ptm"]') ||
      link.parentElement
    );
  }

  function findMonthlyAmountElement(wrapper) {
    if (!isElement(wrapper)) return null;
    const candidates = Array.from(wrapper.querySelectorAll('span,strong,b,em,i'));
    return candidates.find((el) => /\/(mo|month)\b/i.test((el.textContent || '').trim())) ||
      candidates.find((el) => /\$\s*\d/i.test((el.textContent || '').trim())) ||
      null;
  }

  // PayTomorrow sometimes injects non-breaking spaces (\u00A0) or other
  // whitespace that can render as large gaps. Normalize text nodes to single
  // regular spaces so the offer reads "<amount> with PayTomorrow Learn More".
  function normalizeSpaces(root) {
    if (!isElement(root)) return;
    try {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      let node;
      while ((node = walker.nextNode())) {
        const v = node.nodeValue;
        if (!v) continue;
        // Convert NBSP runs to a single space, then collapse multiple spaces/tabs.
        const nv = v.replace(/\u00a0+/g, ' ').replace(/[ \t]{2,}/g, ' ');
        if (nv !== v) node.nodeValue = nv;
      }
    } catch (e) {
      // no-op (older browsers / unexpected DOM)
    }
  }

  function setInlineStyles(wrapper, baseSizePx, linkEl) {
    if (!isElement(wrapper)) return;

    // Keep everything on one line
    wrapper.style.display = 'inline-flex';
    wrapper.style.alignItems = 'baseline';
    wrapper.style.justifyContent = 'flex-start';
    wrapper.style.gap = '6px';
    wrapper.style.flexWrap = 'nowrap';
    wrapper.style.whiteSpace = 'nowrap';
    wrapper.style.lineHeight = '1.2';
    wrapper.style.width = 'auto';

    // Defensive: if any child was pushed away using auto margins, reset it.
    Array.from(wrapper.children || []).forEach((child) => {
      if (!isElement(child)) return;
      const cs = window.getComputedStyle(child);
      if (cs.marginLeft === 'auto') child.style.marginLeft = '0';
      if (cs.marginRight === 'auto') child.style.marginRight = '0';
    });

    // Apply font sizing to key parts
    const spans = Array.from(wrapper.querySelectorAll('span,strong,b,em,i'));
    spans.forEach((el) => {
      const txt = (el.textContent || '').trim().toLowerCase();
      if (txt === 'with' || txt.includes('/mo') || txt.includes('/month')) {
        el.style.fontSize = `${baseSizePx}px`;
        el.style.lineHeight = '1.2';
      }
    });

    if (isElement(linkEl)) {
      linkEl.style.fontSize = `${baseSizePx}px`;
      linkEl.style.lineHeight = '1.2';
      linkEl.style.color = LEARN_MORE_COLOR;
      linkEl.style.fontWeight = '600';
      linkEl.style.textDecoration = 'none';
      linkEl.style.whiteSpace = 'nowrap';
    }
  }

  function hideDefaultTotalPriceIfNeeded(priceContainer) {
    if (!isElement(priceContainer)) return;
    const totalWrap = priceContainer.querySelector('.pt-total-price-wrap');
    if (totalWrap) totalWrap.style.display = 'none';
  }

  function fixOnePriceContainer(priceContainer) {
    if (!isElement(priceContainer)) return;

    const ptLink = findPTLink(priceContainer);
    if (!ptLink) return; // no PayTomorrow offer found

    const offerWrapper = getOfferWrapperFromLink(ptLink);
    if (!offerWrapper) return;

    // Clean up any injected whitespace before applying layout rules
    normalizeSpaces(offerWrapper);

    // Base font size: detect the element that contains $/mo, then add +4px
    const amountEl = findMonthlyAmountElement(offerWrapper) || priceContainer;
    const computed = window.getComputedStyle(amountEl);
    const currentSize = parseFloat(computed.fontSize) || 14;
    const targetSize = Math.max(16, currentSize + 4);

    // Ensure the total price block doesn't show when PayTomorrow exists
    hideDefaultTotalPriceIfNeeded(priceContainer);

    // Apply inline/size/color rules
    setInlineStyles(offerWrapper, targetSize, ptLink);
  }

  function runFix() {
    injectStyles();

    // Common places for price blocks in this theme:
    // - product page: .price__container
    // - cards: .card__information .price__container (varies)
    const priceContainers = document.querySelectorAll(
      '.price__container, .price, .card__information, .product__info-container, .product__info-wrapper'
    );

    priceContainers.forEach((c) => fixOnePriceContainer(c));
  }

  // Run on load
  document.addEventListener('DOMContentLoaded', () => {
    runFix();

    // Watch for late-inserted PayTomorrow markup
    const observer = new MutationObserver(() => runFix());
    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Stop observing after a short window to avoid long-term overhead
    setTimeout(() => observer.disconnect(), 15000);
  });
})();
